import { NgTemplateOutlet } from '@angular/common';
import type { SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
  input,
} from '@angular/core';

import { encodeHTML } from '@core/utils-legacy';

import type { Suggestion } from './typeahead.service';
import { DropdownItemComponent } from '../../common/dropdown/dropdown-item/dropdown-item.component';
import { SpinnerComponent } from '../../common/spinner/spinner.component';
import { ScrollableDirective } from '../../core/overlay/scroll/scrollable.directive';

export interface SuggestionItemContext<S = Suggestion> {
  highlighted: string | null;
  suggestion: S;
  label: string;
  select: (suggestion: S) => void;
}

@Component({
  selector: 'rt-typeahead-suggestions',
  styleUrls: ['./typeahead-suggestions.component.less'],
  templateUrl: './typeahead-suggestions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DropdownItemComponent, NgTemplateOutlet, ScrollableDirective, SpinnerComponent],
})
export class TypeaheadSuggestionsComponent {
  public readonly stateName = input<
    'HIDDEN_STATE' | 'LOADING_STATE' | 'EMPTY_STATE' | 'SUGGESTIONS_STATE'
  >(undefined!);
  public readonly stateValue = input<string | null>(undefined!);
  public readonly suggestions = input<Suggestion[]>(undefined!);
  public readonly highlighted = input<string>(undefined!);
  public readonly maxHeight = input<string>('300px');
  public readonly minHeight = input<string>('auto');
  public readonly limit = input<number>(20);
  public readonly wrapBody = input<boolean>(true);
  public readonly suggestionItemTemplate = input<TemplateRef<SuggestionItemContext>>(undefined!);
  public readonly suggestionListTemplate = input<
    TemplateRef<{
      suggestions: Suggestion[];
      highlighted: string | null;
    }>
  >(undefined!);
  public readonly spinnerTemplate = input<TemplateRef<any>>();
  @Input() public page: number = 1;

  public suggestionSelect = output<Suggestion>();

  public suggestionsToDisplay: Suggestion[] = [];

  constructor(private cdRef: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.suggestions) {
      this.page = 1;
      this.suggestionsToDisplay = changes.suggestions.currentValue.slice(
        0,
        this.limit() * this.page,
      );
    }
  }

  public trackSuggestion(index: number, suggestion: Suggestion) {
    return suggestion.id;
  }

  public highlightValue(value: string, query: string | null): string {
    if (query && query?.length > 0) {
      return value.split(query).map(encodeHTML).join(`<span class="mark">${query}</span>`);
    }
    return encodeHTML(value);
  }

  public onReachEnd(): void {
    if (this.canLoadMore()) {
      this.loadMore();
    }
  }

  private loadMore(): void {
    this.page = this.page + 1;
    this.suggestionsToDisplay = this.suggestions().slice(0, this.limit() * this.page);
    this.cdRef.detectChanges();
  }

  private canLoadMore(): boolean {
    return this.limit() * this.page < this.suggestions().length;
  }
}
